import React, { useState, useEffect, useRef } from 'react';
import { Card, CardBody, Col, Row, Button, Input, Label, Spinner, Badge, Table } from 'reactstrap';
import { Printer, Download, MapPin, Truck, Calendar, AlertCircle, CheckCircle, Clock } from 'react-feather';
import { useReactToPrint } from 'react-to-print';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { apiPost } from '../../../Utils';
import Breadcrumbs from '../../../CommonElements/Breadcrumbs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import DataTable from 'react-data-table-component';
import { useAuth } from '../../../Context/AuthContext';
const RenewalReport = () => {
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState<any[]>([]);
    const [branches, setBranches] = useState<any[]>([]);
    const [vehicles, setVehicles] = useState<any[]>([]);
    const { hasPermission } = useAuth();
    // Filters
    const [filters, setFilters] = useState({
        branch_id: '',
        vehicle_id: '',
        type: '',
        from_date: '',
        to_date: '',
    });

    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);

    // Pagination & Sort State
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [sortColumn, setSortColumn] = useState('date');
    const [sortDirection, setSortDirection] = useState('asc');

    const componentRef = useRef(null);
    const [printData, setPrintData] = useState<any[]>([]);
    const printRef = useRef(null);

    // Fetch Initial Data
    useEffect(() => {
        apiPost("branches/list").then((res: any) => setBranches(Array.isArray(res) ? res : [])).catch(err => console.error(err));
    }, []);

    // Fetch Vehicles when Branch changes
    useEffect(() => {
        const payload: any = {};
        if (filters.branch_id) payload.branch_id = filters.branch_id;
        setFilters(prev => ({ ...prev, vehicle_id: '' }));

        apiPost("vehicles/list", payload)
            .then((res: any) => setVehicles(Array.isArray(res) ? res : []))
            .catch(err => {
                console.error(err);
                setVehicles([]);
            });
    }, [filters.branch_id]);

    const formatDate = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const fetchReport = (page: number, size: number, sortCol: string, sortDir: string) => {
        setLoading(true);
        const payload = {
            ...filters,
            from_date: startDate ? formatDate(startDate) : '',
            to_date: endDate ? formatDate(endDate) : '',
            page: page,
            per_page: size,
            sort_by: sortCol,
            sort_order: sortDir
        };

        apiPost("reports/renewal", payload)
            .then((res: any) => {
                setReportData(res.data || []);
                setTotalRows(res.total || 0);
                setLoading(false);
            })
            .catch(err => {
                console.error(err);
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchReport(currentPage, perPage, sortColumn, sortDirection);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, perPage, sortColumn, sortDirection, filters.branch_id, filters.vehicle_id, filters.type, startDate, endDate]);

    const handlePageChange = (page: number) => setCurrentPage(page);
    const handlePerRowsChange = (newPerPage: number, page: number) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
    };

    const handleSort = (column: any, sortDirection: string) => {
        const field = column.sortField || 'date';
        setSortColumn(field);
        setSortDirection(sortDirection);
    };

    const getStatusBadge = (dateStr: string) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const due = new Date(dateStr);
        const diffTime = due.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 0) {
            return <Badge color="danger" className="px-2 py-1"><AlertCircle size={12} className="me-1" /> Overdue</Badge>;
        } else if (diffDays <= 30) {
            return <Badge color="warning" className="text-dark px-2 py-1"><Clock size={12} className="me-1" /> Due Soon ({diffDays} days)</Badge>;
        } else {
            return <Badge color="success" className="px-2 py-1"><CheckCircle size={12} className="me-1" /> Active</Badge>;
        }
    };

    const columns = [
        {
            name: <div>Sl.No</div>,
            cell: (row: any, index: number) => (currentPage - 1) * perPage + index + 1, width: '70px',
        },

        {
            name: <div>Vehicle / Driver</div>,
            selector: (row: any) => row.vehicle_name,
            sortable: true,
            sortField: 'vehicle_id',
            cell: (row: any) => (
                <span className="">
                    <span className="text-primary">{row.vehicle_name}</span>
                    {row.vehicle_deleted_at && <div className="text-danger small" style={{ fontSize: '0.75rem' }}>(Deleted)</div>}
                </span>
            )
        },
        {
            name: <div>Branch</div>,
            selector: (row: any) => row.branch,
            sortable: true,
            sortField: 'branch',
            cell: (row: any) => (
                <span>
                    {row.branch}
                    {row.branch_deleted_at && <span className="text-danger small ms-1">(Deleted)</span>}
                </span>
            )
        },

        {
            name: <div>Type</div>,
            selector: (row: any) => row.type,
            sortable: true,
            sortField: 'type',
            cell: (row: any) => (
                <Badge color={
                    row.type === 'Insurance' ? 'info' :
                        row.type === 'RC Renewal' ? 'primary' :
                            row.type === 'Permit Renewal' ? 'primary' :
                                row.type === 'License' ? 'warning' : 'secondary'
                }>{row.type}</Badge>
            )
        },

        {
            name: <div>Due Date</div>,
            selector: (row: any) => row.date,
            sortable: true,
            sortField: 'date',
            cell: (row: any) => <span className="text-secondary fw-medium">{row.date ? new Date(row.date).toLocaleDateString('en-GB') : '-'}</span>
        },
        {
            name: <div>Status</div>,
            cell: (row: any) => {
                if (row.status_label === 'Overdue') {
                    return <Badge color="danger" className="px-2 py-1"><AlertCircle size={12} className="me-1" /> Overdue</Badge>;
                } else if (String(row.status_label).startsWith('Due Soon')) {
                    return <Badge color="warning" className="text-dark px-2 py-1"><Clock size={12} className="me-1" /> {row.status_label}</Badge>;
                } else {
                    return <Badge color="success" className="px-2 py-1"><CheckCircle size={12} className="me-1" /> {row.status_label || 'Active'}</Badge>;
                }
            },
            sortable: false,
            width: '180px'
        },
    ];

    const handleDownload = async () => {
        setLoading(true);
        try {
            const payload = {
                ...filters,
                from_date: startDate ? formatDate(startDate) : '',
                to_date: endDate ? formatDate(endDate) : '',
                page: 1,
                per_page: totalRows > 0 ? totalRows : 10000, // Fetch all rows
                sort_by: sortColumn,
                sort_order: sortDirection
            };

            const res: any = await apiPost("reports/renewal", payload);
            const allData = res.data || [];

            const doc = new jsPDF() as any;
            doc.text("Renewal & Maintenance Report", 14, 20);
            doc.setFontSize(10);
            doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 26);

            const tableRows = allData.map((item: any, index: number) => {
                let statusLabel = '';
                if (item.status_label) {
                    statusLabel = item.status_label;
                } else {
                    // Fallback if missing
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const due = new Date(item.date);
                    const diffTime = due.getTime() - today.getTime();
                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                    if (diffDays < 0) statusLabel = 'Overdue';
                    else if (diffDays <= 30) statusLabel = `Due Soon (${diffDays} days)`;
                    else statusLabel = 'Active';
                }

                return [
                    index + 1,
                    item.date ? String(item.date).split('T')[0] : '',
                    item.vehicle_name + (item.vehicle_deleted_at ? ' (Deleted)' : ''),
                    item.branch + (item.branch_deleted_at ? ' (Deleted)' : ''),
                    item.type,
                    statusLabel
                ]
            });

            autoTable(doc, {
                startY: 35,
                head: [['Sl.No', 'Due Date', 'Vehicle', 'Branch', 'Type', 'Status']],
                body: tableRows,
            });

            doc.save('Renewal_Report.pdf');

        } catch (error) {
            console.error("Error downloading report", error);
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        onBeforeGetContent: async () => {
            setLoading(true);
            try {
                const payload = {
                    ...filters,
                    from_date: startDate ? formatDate(startDate) : '',
                    to_date: endDate ? formatDate(endDate) : '',
                    page: 1,
                    per_page: totalRows > 0 ? totalRows : 10000,
                    sort_by: sortColumn,
                    sort_order: sortDirection
                };

                const res: any = await apiPost("reports/renewal", payload);
                setPrintData(res.data || []);
                return new Promise((resolve) => {
                    setTimeout(resolve, 500);
                });
            } catch (error) {
                console.error("Error fetching print data", error);
            } finally {
                setLoading(false);
            }
        },
    });

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#e0f2f1',
                color: '#00695c',
                minHeight: '50px',
                borderBottom: '1px solid #e0e0e0',
            },
        },
        headCells: {
            style: {
                fontSize: '14px',
                fontWeight: '600',
            },
        },
        rows: {
            style: {
                minHeight: '50px',
                '&:not(:last-of-type)': {
                    borderBottomStyle: 'solid',
                    borderBottomWidth: '1px',
                    borderBottomColor: '#e0e0e0',
                },
            },
        },
    };

    return (
        <div className="page-body">
            <Breadcrumbs parent="Reports" title="Renewal Report" pageTitle="Renewal Report" />
            <div className="container-fluid" >
                <div className="d-flex justify-content-end gap-2 mb-3" >
                    {hasPermission('renewal-report.download') && (
                        <Button
                            color="success"
                            className="px-3 py-2 rounded-pill d-flex align-items-center gap-2 shadow-sm"
                            onClick={handleDownload}
                        >
                            <Download size={16} /> <span className="fw-bold">Download</span>
                        </Button>
                    )}
                    {hasPermission('renewal-report.print') && (
                        <Button
                            color="primary"
                            className="px-3 py-2 rounded-pill d-flex align-items-center gap-2 shadow-sm"
                            onClick={handlePrint}
                        >
                            <Printer size={16} /> <span className="fw-bold">Print</span>
                        </Button>
                    )}
                </div>
                <Card className="border-0 shadow-sm mb-4 bg-light">
                    <CardBody className="p-4">

                        <Row className="g-3">
                            <Col md={2}>
                                <Label className="small text-secondary fw-semibold mb-1" style={{ color: '#009688' }}>
                                    Select Type
                                </Label>
                                <Input
                                    type="select"
                                    value={filters.type}
                                    onChange={e => {
                                        setFilters({ ...filters, type: e.target.value });
                                        setCurrentPage(1);
                                    }}
                                    bsSize="sm"
                                    className="form-control"
                                    style={{ borderRadius: '8px', border: '1px solid #ced4da', padding: '0.5rem' }}
                                >
                                    <option value="">All Types</option>
                                    <option value="Insurance">Insurance</option>
                                    <option value="RC">RC Renewal</option>
                                    <option value="Permit">Permit Renewal</option>
                                    <option value="License">License Renewal</option>
                                    <option value="Service">Service Due</option>
                                </Input>
                            </Col>
                            <Col md={3}>
                                <Label className="small text-secondary fw-semibold mb-1 d-flex align-items-center gap-1" style={{ color: '#009688' }}>
                                    <MapPin size={14} style={{ color: '#009688' }} />Select Branch
                                </Label>
                                <Input
                                    type="select"
                                    value={filters.branch_id}
                                    onChange={e => {
                                        setFilters({ ...filters, branch_id: e.target.value });
                                        setCurrentPage(1);
                                    }}
                                    bsSize="sm"
                                    className="form-control"
                                    style={{ borderRadius: '8px', border: '1px solid #ced4da', padding: '0.5rem' }}
                                >
                                    <option value="">All Branches</option>
                                    {branches.map(b => <option key={b.id} value={b.id}>{b.branch_name}</option>)}
                                </Input>
                            </Col>
                            <Col md={3}>
                                <Label className="small text-secondary fw-semibold mb-1 d-flex align-items-center gap-1" style={{ color: '#009688' }}>
                                    <Truck size={14} style={{ color: '#009688' }} />Filter Vehicle
                                </Label>
                                <Input
                                    type="select"
                                    value={filters.vehicle_id}
                                    onChange={e => {
                                        setFilters({ ...filters, vehicle_id: e.target.value });
                                        setCurrentPage(1);
                                    }}
                                    bsSize="sm"
                                    className="form-control"
                                    style={{ borderRadius: '8px', border: '1px solid #ced4da', padding: '0.5rem' }}
                                >
                                    <option value="">All Vehicles</option>
                                    {vehicles.map(v => <option key={v.id} value={v.id}>{v.registration_number}</option>)}
                                </Input>
                            </Col>
                            <Col md={2}>
                                <Label className="small text-secondary fw-semibold mb-1 d-flex align-items-center gap-1" style={{ color: '#009688' }}>
                                    <Calendar size={14} style={{ color: '#009688' }} /> From Date
                                </Label>
                                <div className="rounded overflow-hidden">
                                    <DatePicker
                                        className="form-control"
                                        selected={startDate}
                                        onChange={(date) => { setStartDate(date); setCurrentPage(1); }}
                                        dateFormat="dd/MM/yyyy"
                                        placeholderText="Start Date"
                                        showMonthDropdown
                                        showYearDropdown
                                        dropdownMode="select"
                                    />
                                </div>
                            </Col>
                            <Col md={2}>
                                <Label className="small text-secondary fw-semibold mb-1 d-flex align-items-center gap-1" style={{ color: '#009688' }}>
                                    <Calendar size={14} style={{ color: '#009688' }} /> To Date
                                </Label>
                                <div className="rounded overflow-hidden">
                                    <DatePicker
                                        className="form-control"
                                        selected={endDate}
                                        onChange={(date) => { setEndDate(date); setCurrentPage(1); }}
                                        dateFormat="dd/MM/yyyy"
                                        placeholderText="End Date"
                                        showMonthDropdown
                                        showYearDropdown
                                        dropdownMode="select"
                                    />
                                </div>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                {loading ? <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}>
                    <Spinner color="primary" />
                </div> : (
                    <div ref={componentRef}>
                        <div className="d-none d-print-block text-center mb-4">
                            <h4>Renewal Report</h4>
                            <p className="text-muted">Generated: {new Date().toLocaleDateString()}</p>
                        </div>
                        <Card className="border-0 shadow-sm">
                            <CardBody className="p-0">
                                <DataTable
                                    columns={columns}
                                    data={reportData}
                                    pagination
                                    paginationServer
                                    paginationTotalRows={totalRows}
                                    paginationDefaultPage={currentPage}
                                    onChangePage={handlePageChange}
                                    onChangeRowsPerPage={handlePerRowsChange}
                                    sortServer
                                    onSort={handleSort}
                                    highlightOnHover
                                    striped
                                    responsive
                                    customStyles={customStyles}
                                    noDataComponent={<div className="p-4 text-center text-muted">No upcoming renewals found</div>}
                                />
                            </CardBody>
                        </Card>
                    </div>
                )}

                {/* Print Component (Hidden) */}
                <div style={{ display: 'none' }}>
                    <div ref={printRef} className="p-4">
                        <div className="text-center mb-4">
                            <h2 className="mb-2">Renewal & Maintenance Report</h2>
                            <p className="text-muted">Generated on {new Date().toLocaleDateString()}</p>
                            {startDate && endDate && (
                                <p className="text-muted small">Period: {startDate.toLocaleDateString()} to {endDate.toLocaleDateString()}</p>
                            )}
                        </div>
                        <Table bordered striped responsive className="w-100">
                            <thead className="bg-light">
                                <tr>
                                    <th>Sl.No</th>
                                    <th>Branch</th>
                                    <th>Vehicle/Driver</th>
                                    <th>Type</th>
                                    <th>Due Date</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {printData.map((item, index) => {
                                    let statusLabel = '';
                                    if (item.status_label) {
                                        statusLabel = item.status_label;
                                    } else {
                                        const today = new Date();
                                        today.setHours(0, 0, 0, 0);
                                        const due = new Date(item.date);
                                        const diffTime = due.getTime() - today.getTime();
                                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                                        if (diffDays < 0) statusLabel = 'Overdue';
                                        else if (diffDays <= 30) statusLabel = `Due Soon (${diffDays} days)`;
                                        else statusLabel = 'Active';
                                    }

                                    return (
                                        <tr key={index}>
                                            <td>{index + 1}</td>
                                            <td>
                                                {item.branch || '-'}
                                                {item.branch_deleted_at && <span className="text-danger ms-1">(Deleted)</span>}
                                            </td>
                                            <td>
                                                <span className="fw-bold">{item.vehicle_name || '-'}</span>
                                                {item.vehicle_deleted_at && <div className="text-danger small">(Deleted)</div>}
                                            </td>
                                            <td>{item.type}</td>
                                            <td>{item.date ? new Date(item.date).toLocaleDateString('en-GB') : '-'}</td>
                                            <td>{statusLabel}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                        <div className="mt-4 text-end text-muted small">
                            <p>End of Report</p>
                        </div>
                    </div>
                </div>
            </div>
            <style>
                {`
                    @media print {
                        .d-print-none { display: none !important; }
                        .d-print-block { display: block !important; }
                        .page-body { padding: 0; margin: 0; }
                        .card { border: none !important; box-shadow: none !important; }
                    }
                `}
            </style>
        </div>
    );
};

export default RenewalReport;
